'use client'
import { useState, useEffect, useRef } from 'react';
import { useCart } from '@/contexts/CartContext';
import { usePromo } from '@/contexts/PromoContext';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { encryptBookingParams } from '@/utils/encryption';
import { useAnalytics } from '@/components/Analytics';
import { validateCartRates } from '@/services/api';
import { logoutMember } from '@/services/memberLoginApi';
import { useLoading } from '@/contexts/LoadingContext';
import { validatePromoCode } from '@/services/promocode-api';
import { isValidDateString } from '@/utils/dateUtils';

const FloatingCart = ({ loggedInMember, setLoggedInMember }) => {
  const { activePromo: promoContextData } = usePromo();
  const { 
    cartItems, 
    removeFromCart, 
    clearCart,
    updateRoomQuantity,
    getCartTotal, 
    getCartOriginalTotal,
    getCartPromoDiscount,
    getCartCount, 
    getTotalRoomQuantity,
    isCartOpen, 
    setIsCartOpen
  } = useCart();
  const [isVisible, setIsVisible] = useState(false);
  const [showUpsellDialog, setShowUpsellDialog] = useState(false);
  const { showLoading, hideLoading } = useLoading();
  const [isMounted, setIsMounted] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);
  const [validationMap, setValidationMap] = useState({});
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
  const [promoData, setPromoData] = useState(null); // Store promo data for validation
    
  // State for selected items - auto-select if only 1 item, unselect all if multiple
  const [selectedItems, setSelectedItems] = useState(() => {
    // If there's only 1 item, auto-select it
    if (cartItems.length === 1) {
      return new Set([cartItems[0].cartItemId]);
    }
    // If multiple items, all unselected by default
    return new Set();
  });
  
  const router = useRouter();
  const { trackClick, trackSubmit } = useAnalytics();
  
  // Format price with Indonesian currency format
  const formatPrice = (price) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  // Helper: get booking params from URL or fallbacks
  const getBookingParams = () => {
    let checkIn = null, checkOut = null, guests = '2', property = cartItems[0]?.property_code || '';
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      checkIn = urlParams.get('checkIn');
      checkOut = urlParams.get('checkOut');
      guests = urlParams.get('guests') || '2';
      property = urlParams.get('property') || property;
    }
    return { checkIn, checkOut, guests, property };
  };

  // Compute expected nett per room for date range
  const computeExpectedTotalNettSingle = (item) => {
    const nightlyRate = item?.rate_details?.net_final_rate ?? item?.rate_details?.final_rate ?? 0;
    const nights = item?.nights || 1;
    return Number(nightlyRate) * Number(nights);
  };

  // Helper: Validate promo code restrictions against cart items
  const validatePromoRestrictions = (promoData, itemsToCheckout, checkIn, checkOut) => {
    const errors = [];
    const warnings = [];
    
    if (!promoData) {
      return { valid: false, errors: ['Invalid promo data'], warnings };
    }
    
    console.log('🔍 Validating promo restrictions:', {
      promoCode: promoData.promo_code || promoData.referral_code,
      restrictions: {
        min_nights: promoData.min_nights,
        max_nights: promoData.max_nights,
        min_amount: promoData.min_amount,
        usage_limit: promoData.usage_limit,
        usage_per_guest: promoData.usage_per_guest,
        room_types: promoData.room_types,
        rate_plans: promoData.rate_plans,
        room_type_ids: promoData.room_type_ids,
        rate_plan_ids: promoData.rate_plan_ids,
        stay_start: promoData.stay_start,
        stay_end: promoData.stay_end,
        booking_start: promoData.booking_start,
        booking_end: promoData.booking_end
      }
    });
    
    // Calculate total nights
    const checkInDate = checkIn ? new Date(checkIn) : null;
    const checkOutDate = checkOut ? new Date(checkOut) : null;
    const nights = checkInDate && checkOutDate 
      ? Math.ceil((checkOutDate - checkInDate) / (1000 * 60 * 60 * 24))
      : 0;
    
    // Calculate total amount
    const totalAmount = itemsToCheckout.reduce((sum, item) => {
      const rate = item.promoFinalPrice || item.rate_details?.final_rate || 0;
      return sum + (rate * (item.nights || nights || 1) * (item.quantity || 1));
    }, 0);
    
    // 1. Validate min_nights
    if (promoData.min_nights && nights < promoData.min_nights) {
      errors.push(`Minimum menginap ${promoData.min_nights} malam. Saat ini: ${nights} malam`);
    }
    
    // 2. Validate max_nights
    if (promoData.max_nights && nights > promoData.max_nights) {
      errors.push(`Maximum menginap ${promoData.max_nights} malam. Saat ini: ${nights} malam`);
    }
    
    // 3. Validate min_amount
    if (promoData.min_amount && totalAmount < promoData.min_amount) {
      errors.push(`Minimum pembelian Rp ${formatPrice(promoData.min_amount)}. Saat ini: Rp ${formatPrice(totalAmount)}`);
    }
    
    // 4. Validate usage_limit (total usage)
    const totalUsage = parseInt(promoData.total_usage) || 0;
    const usageLimit = parseInt(promoData.usage_limit) || 0;
    const remainingUsage = parseInt(promoData.remaining_usage) || 0;
    if (usageLimit > 0 && remainingUsage <= 0) {
      errors.push(`Kode promo telah mencapai batas penggunaan total (${totalUsage}/${usageLimit})`);
    }
    
    // 5. Validate usage_per_guest (max times guest can use this promo)
    // NOTE: usage_per_guest according to API means "max times a specific guest can use this promo"
    // This check validates member's usage count against usage_per_guest limit
    // The actual member usage tracking is done in:
    // 1. PromoContext - when loading promo from URL
    // 2. handleValidatePromoCode in reservation page - when manually validating
    // Here we just verify the promo hasn't exceeded total usage limit (already checked above)
    // 
    // If you want to limit rooms per transaction, consider using a different field
    // or creating a separate "max_rooms_per_transaction" field in the promo settings
    console.log(`📊 Usage per guest info: member usage tracking is done during promo validation`);
    
    // 6. Validate room_types
    if (promoData.room_types && promoData.room_types.length > 0) {
      const invalidItems = itemsToCheckout.filter(item => {
        const roomType = item.room_info?.room_type;
        const roomTypeId = item.room_info?.room_id;
        // Check if room type matches
        const matchesRoomType = promoData.room_types.includes(roomType);
        const matchesRoomTypeId = promoData.room_type_ids?.includes(roomTypeId);
        return !matchesRoomType && !matchesRoomTypeId;
      });
      
      if (invalidItems.length > 0) {
        const validRoomTypes = promoData.room_types.join(', ');
        errors.push(`Promo hanya berlaku untuk tipe kamar: ${validRoomTypes}`);
      }
    }
    
    // 7. Validate rate_plans (flexible matching)
    // The ratecode might be "BEST_DEAL_WB" but promo has "BEST_DEAL"
    if (promoData.rate_plans && promoData.rate_plans.length > 0) {
      const invalidItems = itemsToCheckout.filter(item => {
        const rateCode = item.rate_details?.ratecode;
        const ratePlanId = item.rate_details?.rate_plan_id;
        // Check if rate plan matches (flexible matching)
        const matchesRatePlan = promoData.rate_plans.some(rp =>
          rateCode === rp ||
          rateCode?.startsWith(rp) ||
          rateCode?.includes(rp)
        );
        const matchesRatePlanId = promoData.rate_plan_ids?.includes(ratePlanId);
        return !matchesRatePlan && !matchesRatePlanId;
      });
      
      if (invalidItems.length > 0) {
        const validRatePlans = promoData.rate_plans.join(', ');
        errors.push(`Promo hanya berlaku untuk rate plan: ${validRatePlans}`);
      }
    }
    
    // 8. Validate stay dates (stay_start, stay_end) — guest must stay within these dates
    const stayStartValid = isValidDateString(promoData.stay_start);
    const stayEndValid = isValidDateString(promoData.stay_end);
    if (stayStartValid || stayEndValid) {
      const stayStart = stayStartValid ? new Date(promoData.stay_start) : null;
      const stayEnd = stayEndValid ? new Date(promoData.stay_end) : null;

      if (stayStart && checkInDate && checkInDate < stayStart) {
        const stayStartStr = stayStart.toLocaleDateString('id-ID');
        errors.push(`Tanggal check-in harus setelah ${stayStartStr}`);
      }
      if (stayEnd && checkOutDate && checkOutDate > stayEnd) {
        const stayEndStr = stayEnd.toLocaleDateString('id-ID');
        errors.push(`Tanggal check-out harus sebelum ${stayEndStr}`);
      }
    }

    // 9. Validate booking dates (booking_start, booking_end) — guest must BOOK within these dates
    const bookingStartValid = isValidDateString(promoData.booking_start);
    const bookingEndValid = isValidDateString(promoData.booking_end);
    if (bookingStartValid || bookingEndValid) {
      const now = new Date();
      const bookingStart = bookingStartValid ? new Date(promoData.booking_start) : null;
      const bookingEnd = bookingEndValid ? new Date(promoData.booking_end) : null;

      if (bookingStart && now < bookingStart) {
        const bookingStartStr = bookingStart.toLocaleDateString('id-ID');
        errors.push(`Promo berlaku mulai ${bookingStartStr}`);
      }
      if (bookingEnd && now > bookingEnd) {
        const bookingEndStr = bookingEnd.toLocaleDateString('id-ID');
        errors.push(`Promo telah berakhir pada ${bookingEndStr}`);
      }
    }
    
    console.log('📋 Promo validation result:', { 
      valid: errors.length === 0, 
      errors, 
      warnings,
      nights,
      totalAmount
    });
    
    return {
      valid: errors.length === 0,
      errors,
      warnings,
      nights,
      totalAmount
    };
  };

  // Validate a single cart item
  // Check if an item has validation issues
  const hasValidationIssues = (item) => {
    const result = validationMap[item.cartItemId];
    if (!result) return false;
    return result.status !== 'success';
  };

  // Check if any selected item has validation issues
  const hasSelectedValidationIssues = () => {
    const selectedItemsArray = getSelectedItemsArray();
    return selectedItemsArray.some(item => hasValidationIssues(item));
  };

  const validateItem = async (item) => {
    const { checkIn, checkOut, property } = getBookingParams();
    if (!checkIn || !checkOut || !property) {
      return;
    }
    const expectedTotal = computeExpectedTotalNettSingle(item);
    const res = await validateCartRates({
      roomType: item.room_info?.room_type,
      roomId: item.room_info?.room_id,
      rateCode: item.rate_details?.ratecode,
      checkIn,
      checkOut,
      expectedTotalNett: expectedTotal,
      propertyCode: property
    });
    setValidationMap(prev => ({ ...prev, [item.cartItemId]: res }));
  };

  // Validate all items when cart opens or items change
  useEffect(() => {
    if (isCartOpen && cartItems.length > 0) {
      cartItems.forEach(item => validateItem(item));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCartOpen, cartItems]);
  
  // Track previous cart count to detect when items increase from 1 to multiple
  const prevCartCountRef = useRef(cartItems.length);

  // Update selected items when cart items change
  useEffect(() => {
    const currentCount = cartItems.length;
    const previousCount = prevCartCountRef.current;
    
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      // Remove items that are no longer in cart
      for (const id of newSet) {
        if (!cartItems.find(item => item.cartItemId === id)) {
          newSet.delete(id);
        }
      }
      
      // If transitioning from 1 item to multiple items, clear all selections
      // This ensures default unselect all when multiple items first appear
      if (previousCount === 1 && currentCount > 1) {
        return new Set();
      }
      
      // Auto-select if only 1 item and nothing selected yet
      if (currentCount === 1 && newSet.size === 0) {
        newSet.add(cartItems[0].cartItemId);
      }
      
      return newSet;
    });
    
    // Update ref AFTER the state update logic
    prevCartCountRef.current = currentCount;
  }, [cartItems]);
  
  // Load promo data when component mounts (if promo code in URL)
  useEffect(() => {
    const loadPromoData = async () => {
      const promoCodeFromUrl = typeof window !== 'undefined' 
        ? new URLSearchParams(window.location.search).get('promo_code') 
        : null;
      
      if (promoCodeFromUrl && promoCodeFromUrl.trim() && !promoData) {
        try {
          console.log('🔄 Loading promo data for:', promoCodeFromUrl);
          const promoValidation = await validatePromoCode(promoCodeFromUrl.trim());
          if (promoValidation.success && promoValidation.data) {
            console.log('✅ Loaded promo data for usage_per_guest:', promoValidation.data.usage_per_guest);
            setPromoData(promoValidation.data);
          }
        } catch (error) {
          console.error('Error loading promo data:', error);
        }
      }
    };
    
    loadPromoData();
  }, []); // Run on mount only
  
  // Hide the cart initially, then animate in
  useEffect(() => {
    const timer = setTimeout(() => {
      if (isMounted) {
        setIsVisible(true);
      }
    }, 500);
    
    return () => {
      clearTimeout(timer);
      setIsMounted(false);
    };
  }, [isMounted]);
  
  // Handle the upsell dialog - open it when checkout is clicked
  const handleCheckoutClick = async () => {
    // Track checkout click
    trackClick('cart_checkout_click');
    
    console.log('🛒 Checkout button clicked');
    console.log('Selected items:', selectedItems.size);
    console.log('isCheckoutLoading:', isCheckoutLoading);
    
    // Skip the upsell dialog and go directly to checkout
    try {
      await handleWithoutExtraServices();
    } catch (error) {
      console.error('Error in handleCheckoutClick:', error);
      setErrorMessage('Terjadi kesalahan. Silakan coba lagi.');
      setTimeout(() => setErrorMessage(null), 5000);
      setIsCheckoutLoading(false);
    }
  };

  // Process with extra services (go to upsell page)
  const handleWithExtraServices = async () => {
    const itemsToCheckout = getSelectedItemsArray();
    
    // Validate that at least one item is selected
    if (itemsToCheckout.length === 0) {
      setErrorMessage('Pilih minimal satu kamar untuk checkout');
      setTimeout(() => setErrorMessage(null), 3000);
      return;
    }
    
    // Prevent double-click
    if (isCheckoutLoading) {
      return;
    }
    
    // Validate prices before proceeding to upsell
    setIsCheckoutLoading(true);
    setErrorMessage('Memvalidasi harga...');
    
    try {
      // Validate all selected items
      const validationResults = await Promise.all(
        itemsToCheckout.map(async (item) => {
          const { checkIn, checkOut, property } = getBookingParams();
          if (!checkIn || !checkOut || !property) {
            return { cartItemId: item.cartItemId, status: 'error', code: 'MISSING_DATES' };
          }
          
          const expectedTotal = computeExpectedTotalNettSingle(item);
          const result = await validateCartRates({
            roomType: item.room_info?.room_type,
            roomId: item.room_info?.room_id,
            rateCode: item.rate_details?.ratecode,
            checkIn,
            checkOut,
            expectedTotalNett: expectedTotal,
            propertyCode: property
          });
          
          return { cartItemId: item.cartItemId, ...result };
        })
      );
      
      // Check if any validation failed
      const failedValidations = validationResults.filter(
        r => r.status !== 'success'
      );
      
      if (failedValidations.length > 0) {
        const priceChanged = failedValidations.find(r => r.code === 'PRICE_CHANGED');
        const roomNotFound = failedValidations.find(r => r.code === 'ROOM_NOT_FOUND');
        const fetchFailed = failedValidations.find(r => r.code === 'FETCH_FAILED');
        
        if (priceChanged) {
          setErrorMessage('Harga telah berubah. Silakan validasi ulang keranjang Anda.');
          validationResults.forEach(r => {
            setValidationMap(prev => ({ ...prev, [r.cartItemId]: r }));
          });
          setTimeout(() => setErrorMessage(null), 5000);
          setIsCheckoutLoading(false);
          return;
        } else if (roomNotFound) {
          setErrorMessage('Kamar tidak tersedia lagi. Silakan pilih kamar lain.');
          validationResults.forEach(r => {
            setValidationMap(prev => ({ ...prev, [r.cartItemId]: r }));
          });
          setTimeout(() => setErrorMessage(null), 5000);
          setIsCheckoutLoading(false);
          return;
        } else if (fetchFailed) {
          setErrorMessage('Gagal memvalidasi harga. Silakan coba lagi.');
          validationResults.forEach(r => {
            setValidationMap(prev => ({ ...prev, [r.cartItemId]: r }));
          });
          setTimeout(() => setErrorMessage(null), 5000);
          setIsCheckoutLoading(false);
          return;
        }
      }
      
      // All validations passed
      setErrorMessage(null);
      
      console.log('✅ Price validation passed for all items');
      
      // Update validation map with latest results
      validationResults.forEach(r => {
        setValidationMap(prev => ({ ...prev, [r.cartItemId]: r }));
      });
      
      // Validate promo code if present in URL
      const promoCodeFromUrl = typeof window !== 'undefined' 
        ? new URLSearchParams(window.location.search).get('promo_code') 
        : null;
      
      console.log('🔍 Promo code from URL:', promoCodeFromUrl);
      console.log('📊 Selected items promo status:', itemsToCheckout.map(item => ({
        hasActivePromo: item.promo === 1 || item.activePromo,
        ratecode: item.rate_details?.ratecode
      })));
      
      // Check if any selected item has promotional rate already applied
      const hasPromotionalRate = itemsToCheckout.some(item =>
        item.promo === 1 || item.activePromo
      );
      
      console.log('🏷️ Has promotional rate:', hasPromotionalRate);
      
      // Only validate promo code from URL if:
      // 1. There's an actual promo code in URL (not empty)
      // 2. Items don't already have promotional rates applied (discount already in cart)
      if (promoCodeFromUrl && promoCodeFromUrl.trim() && !hasPromotionalRate) {
        console.log('🔍 Validating promo code from URL:', promoCodeFromUrl);
        setErrorMessage('Memvalidasi kode promo...');
        
        try {
          const promoValidation = await validatePromoCode(promoCodeFromUrl.trim());
          
          if (!promoValidation.success) {
            // Promo code is no longer valid - log warning but don't block checkout
            console.warn('⚠️ Promo code validation failed:', promoValidation.message);
            setErrorMessage(promoValidation.message || 'Kode promo tidak valid');
            setTimeout(() => setErrorMessage(null), 5000);
          } else if (promoValidation.data) {
            // Get booking params for restriction validation
            const { checkIn, checkOut } = getBookingParams();
            
            // Validate all promo restrictions
            const restrictionResult = validatePromoRestrictions(
              promoValidation.data,
              itemsToCheckout,
              checkIn,
              checkOut
            );
            
            if (!restrictionResult.valid) {
              // Show validation errors
              const errorMsg = restrictionResult.errors[0]; // Show first error
              console.warn('⚠️ Promo restriction failed:', errorMsg);
              setErrorMessage(errorMsg);
              setTimeout(() => setErrorMessage(null), 5000);
            } else {
              console.log('✅ Promo code validation passed at upsell:', promoCodeFromUrl);
              setErrorMessage(null);
            }
          }
        } catch (promoError) {
          console.error('Error validating promo code at upsell:', promoError);
          // Don't block checkout for promo validation error
        }
      } else if (hasPromotionalRate) {
        console.log('✅ Items have promotional rates - skipping URL promo code validation');
      }
      
    } catch (error) {
      console.error('Error during price validation:', error);
      setErrorMessage('Terjadi kesalahan saat validasi harga. Silakan coba lagi.');
      setTimeout(() => setErrorMessage(null), 5000);
      setIsCheckoutLoading(false);
      return;
    }
    
    // Define the navigation logic as a function
    const navigateToUpsell = async () => {
      if (typeof window !== 'undefined') {
        try {
          // Clear any stale data to prevent conflicts
          localStorage.removeItem('upsellData');
          
          localStorage.setItem('checkoutItems', JSON.stringify(itemsToCheckout));
          
          const urlParams = new URLSearchParams(window.location.search);
          const checkIn = urlParams.get('checkIn');
          const checkOut = urlParams.get('checkOut');
          const guests = urlParams.get('guests') || '2';
          const property = urlParams.get('property') || itemsToCheckout[0]?.property_code || '';
          const promoCode = urlParams.get('promo_code') || '';
          
          setIsCartOpen(false);
          
          const dataToEncrypt = {
            checkIn,
            checkOut,
            guests,
            property_code: property,
            promo_code: promoCode,
            selectedRooms: itemsToCheckout.map(room => ({
              ...room,
              checkIn,
              checkOut,
              room_info: room.room_info,
              rate_details: {
                ...room.rate_details,
                ratedesc: room.rate_details?.ratedesc || 'RateWeb'
              },
              nights: room.nights || 1,
              property_code: room.property_code || property
            }))
          };
          
          const encryptedData = encryptBookingParams(dataToEncrypt);
          
          
          router.push(`/upsell/?data=${encryptedData}`);
        } catch (error) {
          console.error('Error preparing upsell:', error);
          hideLoading();
        }
      }
    };

    // Show the global loading overlay and pass the navigation logic as the callback
    showLoading('upsell', navigateToUpsell);
  };
  
  // Proceed to checkout without extra services
  const handleWithoutExtraServices = async () => {
    console.log('📦 handleWithoutExtraServices called');
    const itemsToCheckout = getSelectedItemsArray();
    console.log('Items to checkout:', itemsToCheckout.length);
    
    // Validate that at least one item is selected
    if (itemsToCheckout.length === 0) {
      console.log('❌ No items selected');
      setErrorMessage('Pilih minimal satu kamar untuk checkout');
      setTimeout(() => setErrorMessage(null), 3000);
      return;
    }
    
    // Prevent double-click during checkout
    if (isCheckoutLoading) {
      console.log('⏳ Already loading, skipping...');
      return;
    }
    
    console.log('✅ Starting validation...');
    // Validate prices before checkout
    setIsCheckoutLoading(true);
    setErrorMessage('Memvalidasi harga...');
    
    try {
      // Validate all selected items
      const validationResults = await Promise.all(
        itemsToCheckout.map(async (item) => {
          const { checkIn, checkOut, property } = getBookingParams();
          if (!checkIn || !checkOut || !property) {
            return { cartItemId: item.cartItemId, status: 'error', code: 'MISSING_DATES' };
          }
          
          const expectedTotal = computeExpectedTotalNettSingle(item);
          const result = await validateCartRates({
            roomType: item.room_info?.room_type,
            roomId: item.room_info?.room_id,
            rateCode: item.rate_details?.ratecode,
            checkIn,
            checkOut,
            expectedTotalNett: expectedTotal,
            propertyCode: property
          });
          
          return { cartItemId: item.cartItemId, ...result };
        })
      );
      
      // Check if any validation failed
      const failedValidations = validationResults.filter(
        r => r.status !== 'success'
      );
      
      if (failedValidations.length > 0) {
        const priceChanged = failedValidations.find(r => r.code === 'PRICE_CHANGED');
        const roomNotFound = failedValidations.find(r => r.code === 'ROOM_NOT_FOUND');
        const fetchFailed = failedValidations.find(r => r.code === 'FETCH_FAILED');
        
        if (priceChanged) {
          setErrorMessage('Harga telah berubah. Silakan validasi ulang keranjang Anda.');
          // Update validation map
          validationResults.forEach(r => {
            setValidationMap(prev => ({ ...prev, [r.cartItemId]: r }));
          });
          setTimeout(() => setErrorMessage(null), 5000);
          setIsCheckoutLoading(false);
          return;
        } else if (roomNotFound) {
          setErrorMessage('Kamar tidak tersedia lagi. Silakan pilih kamar lain.');
          validationResults.forEach(r => {
            setValidationMap(prev => ({ ...prev, [r.cartItemId]: r }));
          });
          setTimeout(() => setErrorMessage(null), 5000);
          setIsCheckoutLoading(false);
          return;
        } else if (fetchFailed) {
          setErrorMessage('Gagal memvalidasi harga. Silakan coba lagi.');
          validationResults.forEach(r => {
            setValidationMap(prev => ({ ...prev, [r.cartItemId]: r }));
          });
          setTimeout(() => setErrorMessage(null), 5000);
          setIsCheckoutLoading(false);
          return;
        }
      }
      
      // All validations passed
      setErrorMessage(null);
      
      console.log('✅ Price validation passed for all items');
      
      // Update validation map with latest results
      validationResults.forEach(r => {
        setValidationMap(prev => ({ ...prev, [r.cartItemId]: r }));
      });
      
      // Validate promo code if present in URL
      const promoCodeFromUrl = typeof window !== 'undefined' 
        ? new URLSearchParams(window.location.search).get('promo_code') 
        : null;
      
      console.log('🔍 Promo code from URL:', promoCodeFromUrl);
      console.log('📊 Selected items promo status:', itemsToCheckout.map(item => ({
        hasActivePromo: item.promo === 1 || item.activePromo,
        ratecode: item.rate_details?.ratecode
      })));
      
      // Check if any selected item has promotional rate already applied
      const hasPromotionalRate = itemsToCheckout.some(item =>
        item.promo === 1 || item.activePromo
      );
      
      console.log('🏷️ Has promotional rate:', hasPromotionalRate);
      
      // Only validate promo code from URL if:
      // 1. There's an actual promo code in URL (not empty)
      // 2. Items don't already have promotional rates applied (discount already in cart)
      if (promoCodeFromUrl && promoCodeFromUrl.trim() && !hasPromotionalRate) {
        console.log('🔍 Validating promo code from URL:', promoCodeFromUrl);
        setErrorMessage('Memvalidasi kode promo...');
        
        try {
          const promoValidation = await validatePromoCode(promoCodeFromUrl.trim());
          
          if (!promoValidation.success) {
            // Promo code is no longer valid - log warning but don't block checkout
            console.warn('⚠️ Promo code validation failed:', promoValidation.message);
            setErrorMessage(promoValidation.message || 'Kode promo tidak valid');
            setTimeout(() => setErrorMessage(null), 5000);
          } else if (promoValidation.data) {
            // Get booking params for restriction validation
            const { checkIn, checkOut } = getBookingParams();
            
            // Validate all promo restrictions
            const restrictionResult = validatePromoRestrictions(
              promoValidation.data,
              itemsToCheckout,
              checkIn,
              checkOut
            );
            
            if (!restrictionResult.valid) {
              // Show validation errors
              const errorMsg = restrictionResult.errors[0]; // Show first error
              console.warn('⚠️ Promo restriction failed:', errorMsg);
              setErrorMessage(errorMsg);
              setTimeout(() => setErrorMessage(null), 5000);
            } else {
              console.log('✅ Promo code validation passed at checkout:', promoCodeFromUrl);
              setErrorMessage(null);
            }
          }
        } catch (promoError) {
          console.error('Error validating promo code at checkout:', promoError);
          // Don't block checkout for promo validation error
        }
      } else if (hasPromotionalRate) {
        console.log('✅ Items have promotional rates - skipping URL promo code validation');
      }
      
    } catch (error) {
      console.error('Error during price validation:', error);
      setErrorMessage('Terjadi kesalahan saat validasi harga. Silakan coba lagi.');
      setTimeout(() => setErrorMessage(null), 5000);
      setIsCheckoutLoading(false);
      return;
    }
    
    trackSubmit('begin_checkout');

    // Define the navigation logic as a function
    const navigateToCheckout = async () => {
      if (typeof window !== 'undefined') {
        try {
          // Clear any stale upsell data to prevent conflicts
          localStorage.removeItem('upsellData');
          
          localStorage.setItem('checkoutItems', JSON.stringify(itemsToCheckout));

          const urlParams = new URLSearchParams(window.location.search);
          const checkIn = urlParams.get('checkIn');
          const checkOut = urlParams.get('checkOut');
          const guests = urlParams.get('guests') || '2';
          const property = urlParams.get('property') || itemsToCheckout[0]?.property_code || '';
          const promoCode = urlParams.get('promo_code') || '';

          setIsCartOpen(false);

          console.log('=== NAVIGATE TO CHECKOUT (FloatingCart) ===');
          console.log('Selected items:', itemsToCheckout);
          console.log('First item rate_details:', itemsToCheckout[0]?.rate_details);
          console.log('Promo code from URL:', promoCode);
          console.log('==========================================');

          const dataToEncrypt = {
            checkIn,
            checkOut,
            guests,
            property_code: property,
            promo_code: promoCode,
            selectedRooms: itemsToCheckout.map(room => ({
              ...room,
              checkIn,
              checkOut,
              room_info: room.room_info,
              rate_details: {
                ...room.rate_details,
                ratecode: room.rate_details?.ratecode,
                ratedesc: room.rate_details?.ratedesc || 'RateWeb',
                include_breakfast: room.rate_details?.include_breakfast,
                breakfast_rate: room.rate_details?.breakfast_rate,
                final_rate: room.rate_details?.final_rate,
                net_final_rate: room.rate_details?.net_final_rate,
                benefits: room.rate_details?.benefits,
                requirements: room.rate_details?.requirements,
                color: room.rate_details?.color
              },
              // Preserve promo pricing fields
              promo: room.promo,
              activePromo: room.activePromo,
              promoDiscount: room.promoDiscount,
              promoFinalPrice: room.promoFinalPrice,
              promoDiscountType: room.promoDiscountType,
              promoTotalDiscount: room.promoTotalDiscount,
              promoIsPerNight: room.promoIsPerNight,
              nights: room.nights || 1,
              property_code: room.property_code || property
            }))
          };

          const encryptedData = encryptBookingParams(dataToEncrypt);
          
          
          router.push(`/checkout/?data=${encryptedData}`);
        } catch (error) {
          console.error('Error preparing checkout:', error);
          // In case of error, hide loading immediately
          hideLoading(); 
          setIsCheckoutLoading(false);
        }
      }
    };

    console.log('🚀 All validations passed, showing loading overlay...');
    // Show the global loading overlay and pass the navigation logic as the callback
    showLoading('checkout', navigateToCheckout);
    // Note: isCheckoutLoading will be reset when cart closes or component remounts
  };

  // Always show if user is logged in
  const shouldShow = loggedInMember || (getCartCount() > 0 || isCartOpen);
  
  if (!shouldShow) {
    return null;
  }
  
  // Handle logout
  const handleLogout = () => {
    logoutMember();
    setLoggedInMember(null);
  };
  
  // Function to handle cart open
  const handleCartOpen = () => {
    trackClick('open_cart');
    setIsCartOpen(true);
    setIsCheckoutLoading(false); // Reset loading state when opening cart
  };
  
  // Function to handle cart close
  const handleCartClose = () => {
    trackClick('close_cart');
    setIsCartOpen(false);
    setIsCheckoutLoading(false); // Reset loading state when closing cart
  };

  
  // Function to handle remove from cart
  const handleRemoveItem = (item) => {
    trackClick(`remove_from_cart_${item.room_info?.room_type}`);
    removeFromCart(item.cartItemId);
  };
  
  // Function to handle quantity increase
  const handleIncreaseQuantity = async (item) => {
    const currentQuantity = item.quantity || 1;
    const availableRooms = item.room_info?.rooms_available || item.rooms_available || 0;
    
    console.log('=== INCREASE QUANTITY DEBUG ===');
    console.log('Current quantity:', currentQuantity);
    console.log('Available rooms:', availableRooms);
    console.log('Promo data:', promoData);
    console.log('Promo context data:', promoContextData);
    console.log('==============================');
    
    // Calculate current total room quantity
    const currentTotalQuantity = cartItems.reduce((sum, cartItem) => {
      return sum + (cartItem.quantity || 1);
    }, 0);
    
    // NOTE: usage_per_guest means "max times a specific guest can use this promo"
    // It is NOT a room quantity limit per transaction.
    // Member usage tracking is handled during promo validation (PromoContext and reservation page)
    // We no longer block room quantity increases based on usage_per_guest.
    // If you need a room quantity limit, create a separate "max_rooms_per_transaction" field.
    
    const result = updateRoomQuantity(item.cartItemId, currentQuantity + 1);
    
    if (!result.success) {
      // Show error message
      setErrorMessage(result.error);
      // Clear error after 3 seconds
      setTimeout(() => setErrorMessage(null), 3000);
    } else {
      // Clear any existing error
      setErrorMessage(null);
      trackClick(`increase_room_quantity_${item.room_info?.room_type}`);
      
      // Validate promo code restrictions after quantity change
      await validatePromoOnCartChange();
    }
  };
  
  // Function to handle quantity decrease
  const handleDecreaseQuantity = async (item) => {
    const currentQuantity = item.quantity || 1;
    const newQuantity = currentQuantity - 1;
    
    // If new quantity would be 0, remove the item from cart
    if (newQuantity === 0) {
      trackClick(`remove_from_cart_${item.room_info?.room_type}`);
    }
    
    // Update quantity (will auto-remove if quantity < 1)
    const result = updateRoomQuantity(item.cartItemId, newQuantity);
    
    if (result && !result.success) {
      setErrorMessage(result.error);
      setTimeout(() => setErrorMessage(null), 3000);
    } else {
      setErrorMessage(null);
      if (newQuantity > 0) {
        trackClick(`decrease_room_quantity_${item.room_info?.room_type}`);
      }
      
      // Validate promo code restrictions after quantity change
      await validatePromoOnCartChange();
    }
  };
  
  // Validate promo code when cart changes (quantity, selection)
  const validatePromoOnCartChange = async () => {
    // Check if there's a promo code in URL
    const promoCodeFromUrl = typeof window !== 'undefined' 
      ? new URLSearchParams(window.location.search).get('promo_code') 
      : null;
    
    if (!promoCodeFromUrl || !promoCodeFromUrl.trim()) {
      return; // No promo code to validate
    }
    
    // Get selected items for validation
    const selectedItemsArray = getSelectedItemsArray();
    if (selectedItemsArray.length === 0) {
      return; // No items selected
    }
    
    // Check if items have promotional rates (skip validation if so)
    const hasPromotionalRate = selectedItemsArray.some(item =>
      item.promo === 1 || item.activePromo
    );
    
    if (hasPromotionalRate) {
      return; // Promotional rates already applied
    }
    
    try {
      const promoValidation = await validatePromoCode(promoCodeFromUrl.trim());
      
      if (promoValidation.success && promoValidation.data) {
        // Store promo data for use in quantity validation
        setPromoData(promoValidation.data);
        
        const { checkIn, checkOut } = getBookingParams();
        
        // Validate all promo restrictions
        const restrictionResult = validatePromoRestrictions(
          promoValidation.data,
          selectedItemsArray, // Use selected items for validation
          checkIn,
          checkOut
        );
        
        if (!restrictionResult.valid) {
          // Show warning but don't block
          const errorMsg = restrictionResult.errors[0];
          console.warn('⚠️ Promo restriction warning:', errorMsg);
          setErrorMessage(`⚠️ ${errorMsg}`);
          setTimeout(() => setErrorMessage(null), 5000);
        }
      }
    } catch (error) {
      console.error('Error validating promo on cart change:', error);
    }
  };
  
  // Toggle item selection
  const toggleItemSelection = async (cartItemId) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(cartItemId)) {
        newSet.delete(cartItemId);
      } else {
        newSet.add(cartItemId);
      }
      return newSet;
    });
    
    // Validate promo code restrictions after selection change
    // Use setTimeout to ensure state is updated
    setTimeout(() => validatePromoOnCartChange(), 100);
  };
  
  // Select all items
  const selectAllItems = async () => {
    setSelectedItems(new Set(cartItems.map(item => item.cartItemId)));
    
    // Validate promo code restrictions after selection change
    setTimeout(() => validatePromoOnCartChange(), 100);
  };
  
  // Unselect all items
  const unselectAllItems = async () => {
    setSelectedItems(new Set());
    
    // Clear any promo warning messages
    setErrorMessage(null);
  };
  
  // Get selected items array
  const getSelectedItemsArray = () => {
    return cartItems.filter(item => selectedItems.has(item.cartItemId));
  };
  
  // Check if quantity can be increased
  // NOTE: usage_per_guest is NOT a room quantity limit - it tracks how many times
  // a specific member has used this promo code. This check always allows increasing quantity.
  const canIncreaseQuantity = (item) => {
    // Member usage tracking is done during promo validation
    // Always allow quantity increases - the promo restriction is about usage count, not room count
    return true;
  };
  
  // Get usage limit info for display
  // NOTE: usage_per_guest tracks member usage, not room quantity limits
  const getUsageLimitInfo = () => {
    // Return null as we don't track room quantity limits here
    // Member usage tracking is done during promo validation
    return null;
  };
  
  // Get total of selected items (with promo discount)
  const getSelectedTotal = () => {
    return getSelectedItemsArray().reduce((total, item) => {
      // Use promo-discounted price if available
      const rate = item.promoFinalPrice || item.rate_details?.final_rate || 0;
      return total + rate * (item.nights || 1) * (item.quantity || 1);
    }, 0);
  };
  
  // Get original total of selected items (before promo discount)
  const getSelectedOriginalTotal = () => {
    return getSelectedItemsArray().reduce((total, item) => {
      const rate = item.rate_details?.final_rate || 0;
      return total + rate * (item.nights || 1) * (item.quantity || 1);
    }, 0);
  };
  
  // Get promo discount for selected items
  const getSelectedPromoDiscount = () => {
    return getSelectedItemsArray().reduce((total, item) => {
      if (item.promo === 1 || item.activePromo) {
        const originalRate = item.rate_details?.final_rate || 0;
        const promoRate = item.promoFinalPrice || originalRate;
        return total + ((originalRate - promoRate) * (item.nights || 1) * (item.quantity || 1));
      }
      return total;
    }, 0);
  };
  
  return (
    <>
      {/* Floating Buttons */}
      <div className="fixed bottom-20 left-4 sm:left-6 z-40 flex flex-col gap-3">
        {/* Profile menu removed */}
        
        {/* Cart Button - Only visible when cart has items */}
        {getCartCount() > 0 && (
          <div 
            className={`flex items-center gap-1 sm:gap-2 bg-white text-gray-800 py-2 px-3 sm:px-4 rounded-full shadow-lg cursor-pointer transition-all duration-500 ${
              isVisible 
                ? 'translate-y-0 opacity-100' 
                : 'translate-y-20 opacity-0'
            } ${isCartOpen ? 'scale-0 opacity-0' : 'scale-100'}`}
            onClick={handleCartOpen}
            style={{
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              border: '1px solid rgba(0,0,0,0.05)'
            }}
          >
            <div className="relative">
              <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <span className="absolute -top-2 -right-2 bg-red-600 text-white text-[10px] sm:text-xs font-bold rounded-full w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center">
                {selectedItems.size > 0 ? selectedItems.size : getCartCount()}
              </span>
            </div>
            <span className="font-medium text-sm sm:text-base">Rp {formatPrice(selectedItems.size > 0 ? getSelectedTotal() : getCartTotal())}</span>
          </div>
        )}
      </div>
      
      {/* Cart Panel */}
      <div 
        className={`fixed inset-0 z-50 transition-opacity duration-300 ${
          isCartOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        {/* Backdrop */}
        <div 
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          onClick={handleCartClose}
        ></div>
        
        {/* Cart Panel - Full screen on mobile, slide from bottom on small screens */}
        <div 
          className={`absolute left-0 top-0 bottom-0 w-full max-w-md bg-white shadow-xl transition-transform duration-500 flex flex-col ${
            isCartOpen ? 'translate-x-0' : '-translate-x-full'
          } sm:left-0 sm:top-0 sm:bottom-0`}
        >
          {/* Cart Header - Sticky */}
          <div className="sticky top-0 bg-white z-10 p-3 sm:p-4 border-b flex items-center justify-between">
            <h2 className="text-base sm:text-lg font-semibold flex items-center gap-2">
              <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <span className="hidden sm:inline">Kamar Pilihan Anda</span>
              <span className="sm:hidden">Keranjang</span>
              <span className="text-sm font-normal text-gray-500">({selectedItems.size}/{getCartCount()})</span>
            </h2>
            <div className="flex items-center gap-1 sm:gap-2">
              {/* Clear All Cart Button */}
              {cartItems.length > 0 && (
                <button 
                  onClick={() => {
                    if (confirm('Hapus semua kamar dari keranjang?')) {
                      clearCart();
                    }
                  }}
                  className="px-2 py-1.5 sm:px-3 text-xs font-medium text-red-600 bg-red-50 rounded-md hover:bg-red-100 transition-colors flex items-center gap-1"
                  title="Hapus semua dari keranjang"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  <span className="hidden sm:inline">Hapus Semua</span>
                </button>
              )}
              <button 
                className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                onClick={handleCartClose}
                aria-label="Close cart"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
          
          {/* Select All/Unselect All Buttons */}
          {cartItems.length > 1 && (
            <div className="sticky top-[60px] sm:top-[68px] bg-gray-50 z-10 px-3 py-2 sm:px-4 sm:py-2 border-b flex items-center gap-2 overflow-x-auto">
              <button
                onClick={selectAllItems}
                className="px-2.5 py-1.5 sm:px-3 text-xs font-medium bg-primary text-white rounded-md hover:bg-primary/90 transition-colors whitespace-nowrap"
              >
                Pilih Semua
              </button>
              <button
                onClick={unselectAllItems}
                className="px-2.5 py-1.5 sm:px-3 text-xs font-medium bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors whitespace-nowrap"
              >
                Batalkan
              </button>
              <span className="ml-auto text-xs text-gray-500 whitespace-nowrap">
                {selectedItems.size}/{getCartCount()} dipilih
              </span>
            </div>
          )}
          
          {/* Error Message */}
          {errorMessage && (
            <div className="mx-3 sm:mx-4 mt-3 sm:mt-4 p-2 sm:p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="flex-grow">
                <p className="text-xs sm:text-sm text-amber-800">{errorMessage}</p>
                {errorMessage.includes('Harga telah berubah') && (
                  <button
                    onClick={() => {
                      // Re-validate all items
                      cartItems.forEach(item => validateItem(item));
                      setErrorMessage('Validasi ulang sedang dilakukan...');
                      setTimeout(() => setErrorMessage(null), 3000);
                    }}
                    className="mt-1 text-xs sm:text-sm text-amber-700 underline hover:text-amber-900"
                  >
                    Validasi Ulang Sekarang
                  </button>
                )}
              </div>
            </div>
          )}
          
          {/* Cart Items */}
          <div className="flex-grow overflow-auto p-3 sm:p-4 divide-y">
            {cartItems.length > 0 ? (
              cartItems.map((item) => {
                // Debug log for each cart item
                console.log('=== CART ITEM DEBUG ===');
                console.log('Room:', item.room_info?.room_type);
                console.log('room_info.rooms_available:', item.room_info?.rooms_available);
                console.log('rooms_available:', item.rooms_available);
                console.log('availability:', item.availability);
                console.log('Full item:', item);
                console.log('======================');
                
                return (
                <div key={item.cartItemId} className={`py-3 sm:py-4 first:pt-0 last:pb-0 ${!selectedItems.has(item.cartItemId) ? 'opacity-60' : ''}`}>
                  <div className="flex gap-2 sm:gap-4">
                    {/* Checkbox */}
                    <div className="flex items-center">
                      <label className="relative flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedItems.has(item.cartItemId)}
                          onChange={() => toggleItemSelection(item.cartItemId)}
                          className="sr-only"
                        />
                        <div className={`w-5 h-5 border-2 rounded flex items-center justify-center transition-all ${
                          selectedItems.has(item.cartItemId)
                            ? 'bg-primary border-primary'
                            : 'bg-white border-gray-300 hover:border-primary'
                        }`}>
                          {selectedItems.has(item.cartItemId) && (
                            <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                      </label>
                    </div>
                    
                    {/* Room Image - Smaller on mobile */}
                    <div className="relative w-16 h-16 sm:w-24 sm:h-24 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
                      {item.room_info.images?.[0] ? (
                        <Image
                          src={item.room_info.images[0]}
                          alt={item.room_info.room_type}
                          fill
                          className="object-cover"
                        />
                      ) : (
                        <div className="flex items-center justify-center h-full text-gray-400">
                          <svg className="w-6 h-6 sm:w-8 sm:h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </div>
                      )}
                    </div>
                    
                    {/* Room Details */}
                    <div className="flex-grow min-w-0">
                      <div className="flex justify-between items-start gap-2">
                        <div className="min-w-0">
                          <h3 className="font-medium text-sm sm:text-base text-gray-800 truncate">{item.room_info.room_type}</h3>
                          {/* Rate Plan Badge */}
                          <div className="flex items-center gap-1 sm:gap-2 mt-0.5 sm:mt-1">
                            <span className={`inline-flex items-center px-1.5 py-0.5 sm:px-2 rounded text-[10px] sm:text-xs font-medium ${
                              item.rate_details?.include_breakfast 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {item.rate_details?.include_breakfast ? (
                                <>
                                  <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3 mr-0.5 sm:mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                  <span className="hidden sm:inline">Dengan Sarapan</span>
                                  <span className="sm:hidden">Sarapan</span>
                                </>
                              ) : (
                                <>
                                  <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3 mr-0.5 sm:mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                                  </svg>
                                  Room Only
                                </>
                              )}
                            </span>
                          </div>
                          {/* Validation Indicator */}
                          {validationMap[item.cartItemId] && (
                            (() => {
                              const result = validationMap[item.cartItemId];
                              if (result.status === 'success') {
                                return (
                                  <div className="mt-1 sm:mt-2 inline-flex items-center px-1.5 py-0.5 sm:px-2 rounded text-[10px] sm:text-xs font-medium bg-green-50 text-green-700 border border-green-200">
                                    <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3 mr-0.5 sm:mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    Harga valid
                                  </div>
                                );
                              }
                              if (result.code === 'PRICE_CHANGED') {
                                const newTotalPerRoom = result.data?.total_nett || 0;
                                const nights = item.nights || 1;
                                const newNightRate = nights > 0 ? Math.round(newTotalPerRoom / nights) : newTotalPerRoom;
                                return (
                                  <div className="mt-1 sm:mt-2 inline-flex items-center px-1.5 py-0.5 sm:px-2 rounded text-[10px] sm:text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
                                    <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3 mr-0.5 sm:mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <span className="hidden sm:inline">Harga berubah: Rp {formatPrice(newNightRate)}/malam</span>
                                    <span className="sm:hidden">Rp {formatPrice(newNightRate)}/mlm</span>
                                    <span className="ml-1 sm:ml-2 text-amber-600">(tutup & buka keranjang untuk update)</span>
                                  </div>
                                );
                              }
                              if (result.code === 'MISSING_DATES' || result.code === 'ROOM_NOT_FOUND' || result.code === 'FETCH_FAILED') {
                                return (
                                  <div className="mt-1 sm:mt-2 inline-flex items-center px-1.5 py-0.5 sm:px-2 rounded text-[10px] sm:text-xs font-medium bg-red-50 text-red-700 border border-red-200">
                                    <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3 mr-0.5 sm:mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    Rate tidak tersedia - pilih kamar lain
                                  </div>
                                );
                              }
                              return null;
                            })()
                          )}
                        </div>
                        {/* Remove button */}
                        <button 
                          onClick={() => handleRemoveItem(item)}
                          className="text-gray-400 hover:text-red-500 transition-colors flex-shrink-0 p-1"
                          aria-label="Remove room"
                        >
                          <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>

                      {/* Quantity Controls - Compact on mobile */}
                      <div className="mt-2 sm:mt-3 flex items-center gap-2 sm:gap-3">
                        <span className="text-xs sm:text-sm text-gray-600 font-medium">Jml:</span>
                        <div className="flex items-center border-2 border-gray-300 rounded-lg overflow-hidden shadow-sm">
                          <button
                            onClick={() => handleDecreaseQuantity(item)}
                            className={`px-3 py-1.5 sm:px-4 sm:py-2 bg-white transition-colors border-r-2 border-gray-300 ${
                              (item.quantity || 1) === 1 
                                ? 'text-red-500 hover:bg-red-50 hover:border-red-300' 
                                : 'hover:bg-gray-100 text-gray-700'
                            }`}
                            aria-label="Decrease quantity"
                            title={(item.quantity || 1) === 1 ? 'Hapus dari keranjang' : 'Kurangi jumlah'}
                          >
                            <svg className="w-4 h-4 sm:w-5 sm:h-5 stroke-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M20 12H4" />
                            </svg>
                          </button>
                          <span className="px-4 py-1.5 sm:px-6 sm:py-2 bg-white font-bold text-sm sm:text-base min-w-[40px] sm:min-w-[50px] text-center text-gray-800">
                            {item.quantity || 1}
                          </span>
                          <button
                            onClick={() => handleIncreaseQuantity(item)}
                            disabled={(item.quantity || 1) >= (item.room_info?.rooms_available || item.rooms_available || 999) || !canIncreaseQuantity(item)}
                            className={`px-3 py-1.5 sm:px-4 sm:py-2 bg-white hover:bg-gray-100 transition-colors border-l-2 border-gray-300 ${
                              (item.quantity || 1) >= (item.room_info?.rooms_available || item.rooms_available || 999) || !canIncreaseQuantity(item)
                                ? 'opacity-30 cursor-not-allowed'
                                : 'text-gray-700'
                            }`}
                            aria-label="Increase quantity"
                            title={
                              !canIncreaseQuantity(item) 
                                ? `Promo limit: ${promoData?.usage_per_guest || promoContextData?.usage_per_guest} room(s) per transaction`
                                : `Max: ${item.room_info?.rooms_available || item.rooms_available || 'unlimited'} rooms available`
                            }
                          >
                            <svg className="w-4 h-4 sm:w-5 sm:h-5 stroke-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                            </svg>
                          </button>
                        </div>
                        <span className="text-[10px] sm:text-xs text-gray-500">
                          max: {item.room_info?.rooms_available || item.rooms_available || 999}
                        </span>
                      </div>
                      
                      {/* Price Info */}
                      <div className="mt-2 flex justify-between items-end">
                        <div className="text-[10px] sm:text-xs text-gray-500">
                          {item.nights || 1} malam × {item.quantity || 1} kamar
                        </div>
                        <div className="text-right">
                          {/* Show original price with strikethrough if promo applied */}
                          {(item.promo === 1 || item.activePromo) && item.promoDiscount > 0 && (
                            <div className="text-[10px] sm:text-xs text-gray-400 line-through">
                              Rp {formatPrice((item.rate_details?.final_rate || 0) * (item.nights || 1) * (item.quantity || 1))}
                            </div>
                          )}
                          <div className="font-medium text-sm sm:text-base text-green-600">
                            Rp {formatPrice((item.promoFinalPrice || item.rate_details?.final_rate || 0) * (item.nights || 1) * (item.quantity || 1))}
                          </div>
                          {/* Promo badge */}
                          {(item.promo === 1 || item.activePromo) && (
                            <span className="inline-flex items-center mt-1 px-1.5 py-0.5 rounded text-[9px] font-bold bg-gradient-to-r from-green-500 to-emerald-600 text-white uppercase tracking-wide">
                              Promo
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                );
              })
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-center text-gray-500 py-8 sm:py-10">
                <svg className="w-12 h-12 sm:w-16 sm:h-16 mb-3 sm:mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <h3 className="text-base sm:text-lg font-medium mb-1 sm:mb-2">Keranjang Anda kosong</h3>
                <p className="max-w-xs mx-auto text-xs sm:text-sm">
                  Silakan pilih kamar untuk ditambahkan ke dalam keranjang Anda.
                </p>
                <button 
                  className="mt-4 sm:mt-6 px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors text-sm"
                  onClick={handleCartClose}
                >
                  Lihat Kamar
                </button>
              </div>
            )}
          </div>
          
          {/* Checkout Button - Sticky on mobile */}
          <div className="sticky bottom-0 bg-white border-t mt-auto p-3 sm:p-4">
            {cartItems.length > 0 && (
              <>
                {/* Usage Per Guest Limit Display */}
                {getUsageLimitInfo() && (
                  <div className={`mb-2 p-2 sm:p-3 border rounded-lg ${getUsageLimitInfo().isAtLimit ? 'bg-amber-50 border-amber-200' : 'bg-blue-50 border-blue-200'}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-gradient-to-r from-blue-500 to-indigo-600 text-white uppercase tracking-wide">
                          Limit
                        </span>
                        <span className="text-xs sm:text-sm text-blue-700 font-medium">
                          Promo: {getUsageLimitInfo().current}/{getUsageLimitInfo().limit} room(s)
                        </span>
                      </div>
                      <span className="text-xs text-blue-600">
                        {getUsageLimitInfo().isAtLimit ? 'Limit reached' : `${getUsageLimitInfo().remaining} remaining`}
                      </span>
                    </div>
                  </div>
                )}
                {/* Promo Code Display */}
                {(() => {
                  // Get promo code from URL or context
                  const promoCodeFromUrl = typeof window !== 'undefined' 
                    ? new URLSearchParams(window.location.search).get('promo_code')
                    : null;
                  const activePromoCode = promoContextData?.promo_code || promoCodeFromUrl || promoData?.promo_code;
                  
                  if (!activePromoCode) return null;
                  
                  return (
                    <div className="mb-2 p-2 sm:p-3 bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-gradient-to-r from-purple-500 to-pink-600 text-white uppercase tracking-wide">
                            Promo
                          </span>
                          <span className="text-xs sm:text-sm text-purple-700 font-medium">{activePromoCode}</span>
                        </div>
                        <span className="text-xs text-purple-600">
                          Active
                        </span>
                      </div>
                    </div>
                  );
                })()}
                {/* Promo Discount Display */}
                {getSelectedPromoDiscount() > 0 && (
                  <div className="mb-2 p-2 sm:p-3 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-gradient-to-r from-green-500 to-emerald-600 text-white uppercase tracking-wide">
                          Promo
                        </span>
                        <span className="text-xs sm:text-sm text-green-700 font-medium">Discount Applied!</span>
                      </div>
                      <span className="text-sm sm:text-base font-bold text-green-600">-Rp {formatPrice(getSelectedPromoDiscount())}</span>
                    </div>
                  </div>
                )}
                <div className="flex justify-between items-center mb-2">
                  <span className="text-xs sm:text-sm text-gray-600">
                    {selectedItems.size} kamar dipilih
                  </span>
                  <span className="text-xs sm:text-sm text-gray-600">
                    {getSelectedPromoDiscount() > 0 && (
                      <span className="line-through text-gray-400 mr-2">Rp {formatPrice(getSelectedOriginalTotal())}</span>
                    )}
                    Total: <span className="font-bold text-sm sm:text-base text-gray-900">Rp {formatPrice(getSelectedTotal())}</span>
                  </span>
                </div>
                <button
                  className={`w-full py-2.5 sm:py-3 text-white rounded-lg font-medium text-base sm:text-lg flex items-center justify-center gap-2 transition-all ${
                    selectedItems.size === 0 || isCheckoutLoading || hasSelectedValidationIssues()
                      ? 'bg-gray-300 cursor-not-allowed'
                      : 'bg-green-600 hover:bg-green-700'
                  }`}
                  onClick={handleCheckoutClick}
                  disabled={selectedItems.size === 0 || isCheckoutLoading || hasSelectedValidationIssues()}
                  title={hasSelectedValidationIssues() ? 'Harga perlu validasi. Harga otomatis diperbarui saat membuka keranjang.' : ''}
                >
                  {isCheckoutLoading ? (
                    <>
                      <svg className="animate-spin w-4 h-4 sm:w-5 sm:h-5" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      <span>Validasi Harga...</span>
                    </>
                  ) : (
                    <>
                      <span>{hasSelectedValidationIssues() ? 'Harga Perlu Update' : selectedItems.size === 0 ? 'Pilih Kamar Dulu' : `Checkout (${selectedItems.size})`}</span>
                      {selectedItems.size > 0 && (
                        <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                        </svg>
                      )}
                    </>
                  )}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Checkout Loading Overlay */}
          </>
  );
};

export default FloatingCart; 
